import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Card, CardBody, Button } from "reactstrap";
import { Fn_AddEditData, Fn_DisplayData, Fn_FillListData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useLocation, useNavigate } from "react-router-dom";
import { Save, ArrowLeft, Plus, Trash2, DollarSign } from "react-feather";

const AddEdit_VehicleRateMaster = () => {
  const API_URL_SAVE = "VehicleRateMaster/0/token";
  const API_URL_EDIT = API_WEB_URLS.MASTER + "/0/token/VehicleRateMaster/Id";
  const API_URL = API_WEB_URLS.MASTER + "/0/token/RateDataForEdit/Id";
  const API_URL_VEHICLE_TYPE = API_WEB_URLS.MASTER + "/0/token/VehicleType/Id/0";

  const [state, setState] = useState({
    id: 0,
    formData: {
      F_VehicleType: "",
      RangeFrom: "",
      RangeTo: "",
      BaseRate: "",
      WaitingPeriod: "",
      WaitingPeriodCharge: "",
      PointWiseRate: "",
      LoadingUnloadingCharge: "",
      FreeLoadingTime: "",
    },
  });

  const [vehicleTypeList, setVehicleTypeList] = useState([]);

  // Dynamic slab rows
  const [slabs, setSlabs] = useState([
    { RangeFrom: "", RangeTo: "", RatePerKm: "" },
  ]);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const vehicleTypeRef = useRef(null);

  useEffect(() => {
    const Id = (location.state && location.state.Id) || 0;
    Fn_FillListData(dispatch, setVehicleTypeList, "gridData", API_URL_VEHICLE_TYPE);

    if (Id > 0) {
      fetchDataByVehicleType(Id);
    }
  }, [dispatch, location.state]);

  useEffect(() => {
    if (vehicleTypeRef.current) vehicleTypeRef.current.focus();
  }, []);

  // ── Slab helpers ─────────────────────────────────────────────────────────
  const handleSlabChange = (index, field, value) => {
    setSlabs((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const fetchDataByVehicleType = async (vehicleTypeId) => {
    if (!vehicleTypeId) {
      setState((prev) => ({
        ...prev,
        id: 0,
        formData: {
          F_VehicleType: "",
          RangeFrom: "",
          RangeTo: "",
          BaseRate: "",
          WaitingPeriod: "",
          WaitingPeriodCharge: "",
          PointWiseRate: "",
        },
      }));
      setSlabs([{ RangeFrom: "", RangeTo: "", RatePerKm: "" }]);
      return;
    }

    await Fn_FillListData(
      dispatch,
      (dataList) => {
        if (!dataList || dataList.length === 0) {
          setState((prev) => ({
            ...prev,
            id: 0,
            formData: {
              F_VehicleType: String(vehicleTypeId),
              RangeFrom: "",
              RangeTo: "",
              BaseRate: "",
              WaitingPeriod: "",
              WaitingPeriodCharge: "",
              PointWiseRate: "",
              LoadingUnloadingCharge: "",
              FreeLoadingTime: "",
            },
          }));
          setSlabs([{ RangeFrom: "", RangeTo: "", RatePerKm: "" }]);
          return;
        }

        const rec = dataList[0];
        setState((prev) => ({
          ...prev,
          id: rec.Id || 0,
          formData: {
            F_VehicleType: rec.F_VehicleType ? String(rec.F_VehicleType) : String(vehicleTypeId),
            RangeFrom: rec.RangeFrom ?? "",
            RangeTo: rec.RangeTo ?? "",
            BaseRate: rec.BaseRate ?? "",
            WaitingPeriod: rec.WaitingPeriod ?? "",
            WaitingPeriodCharge: rec.WaitingPeriodCharge ?? "",
            PointWiseRate: rec.PointWiseRate ?? "",
            LoadingUnloadingCharge: rec.LoadingUnloadingCharge ?? "",
            FreeLoadingTime: rec.FreeLoadingTime ?? "",
          },
        }));

        try {
          const parsed = typeof rec.SlabsJson === 'string' ? JSON.parse(rec.SlabsJson || "[]") : (Array.isArray(rec.SlabsJson) ? rec.SlabsJson : []);
          setSlabs(
            parsed.length > 0
              ? parsed.map((s) => ({
                  RangeFrom: s.RangeFrom ?? "",
                  RangeTo: s.RangeTo ?? "",
                  RatePerKm: s.RatePerKm ?? "",
                }))
              : [{ RangeFrom: "", RangeTo: "", RatePerKm: "" }]
          );
        } catch {
          setSlabs([{ RangeFrom: "", RangeTo: "", RatePerKm: "" }]);
        }
      },
      "gridData",
      API_URL + "/" + vehicleTypeId
    );
  };

  const addSlabRow = () => {
    setSlabs((prev) => [...prev, { RangeFrom: "", RangeTo: "", RatePerKm: "" }]);
  };

  const removeSlabRow = (index) => {
    setSlabs((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validationSchema = Yup.object({
    F_VehicleType: Yup.string().required("Vehicle type is required"),
    RangeFrom: Yup.number()
      .typeError("Must be a number")
      .required("Range From is required")
      .min(0, "Cannot be negative"),
    RangeTo: Yup.number()
      .typeError("Must be a number")
      .required("Range To is required")
      .min(0, "Cannot be negative"),
    BaseRate: Yup.number()
      .typeError("Must be a number")
      .required("Base Rate is required")
      .min(0, "Cannot be negative"),
    WaitingPeriod: Yup.number()
      .typeError("Must be a number")
      .required("Waiting Period is required")
      .integer("Must be a whole number")
      .min(0, "Cannot be negative"),
    WaitingPeriodCharge: Yup.number()
      .typeError("Must be a number")
      .required("Waiting Period Charge is required")
      .min(0, "Cannot be negative"),
    PointWiseRate: Yup.number()
      .typeError("Must be a number")
      .required("Point Wise Rate is required")
      .min(0, "Cannot be negative"),
    LoadingUnloadingCharge: Yup.number()
      .typeError("Must be a number")
      .min(0, "Cannot be negative")
      .nullable(),
    FreeLoadingTime: Yup.number()
      .typeError("Must be a number")
      .min(0, "Cannot be negative")
      .nullable(),
  });

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = (values) => {
    const obj = JSON.parse(localStorage.getItem("user"));

    // Validate slabs minimally — at least one complete row
    const filledSlabs = slabs.filter(
      (s) => s.RangeFrom !== "" && s.RangeTo !== "" && s.RatePerKm !== ""
    );

    const slabPayload = filledSlabs.map((s) => ({
      RangeFrom: parseFloat(s.RangeFrom),
      RangeTo: parseFloat(s.RangeTo),
      RatePerKm: parseFloat(s.RatePerKm),
    }));

    let formData = new FormData();
    formData.append("F_VehicleType", values.F_VehicleType);
    formData.append("RangeFrom", values.RangeFrom);
    formData.append("RangeTo", values.RangeTo);
    formData.append("BaseRate", values.BaseRate);
    formData.append("WaitingPeriod", values.WaitingPeriod);
    formData.append("WaitingPeriodCharge", values.WaitingPeriodCharge);
    formData.append("PointWiseRate", values.PointWiseRate);
    formData.append("LoadingUnloadingCharge", values.LoadingUnloadingCharge || 0);
    formData.append("FreeLoadingTime", values.FreeLoadingTime || 0);
    formData.append("SlabJson", JSON.stringify(slabPayload));
    formData.append("UserId", obj === null || obj === undefined ? 0 : obj.uid);

    Fn_AddEditData(
      dispatch,
      setState,
      { arguList: { id: state.id, formData } },
      API_URL_SAVE,
      true,
      "vehiclerateid",
      navigate,
      "/VehicleRateMaster"
    );
  };

  // ── Field style helpers ───────────────────────────────────────────────────
  const inputStyle = { height: 28, fontSize: 12, padding: "2px 8px", borderRadius: 4 };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: "#555", marginBottom: 2 };
  const errorStyle = { fontSize: 10, color: "#dc3545", marginTop: 1 };

  return (
    <div style={{ padding: "6px 8px", maxWidth: 960, margin: "0 auto" }}>
      <Card className="border shadow-none" style={{ marginBottom: 0 }}>
        <CardBody className="p-2" style={{ padding: "8px 12px !important" }}>

          {/* ── Header ── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
              paddingBottom: 5,
              borderBottom: "1px solid #dee2e6",
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
              Vehicle Rate Master
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <Button
                type="button"
                size="sm"
                color="light"
                className="py-1 px-2"
                style={{ fontSize: 12 }}
                onClick={() => navigate("/dashboard")}
              >
                <ArrowLeft size={12} className="me-1" />Back
              </Button>
              <Button
                type="submit"
                form="vehicle-rate-form"
                size="sm"
                color="primary"
                className="py-1 px-2"
                style={{ fontSize: 12 }}
              >
                <Save size={12} className="me-1" />Save
              </Button>
            </div>
          </div>

          {/* ── Formik Form ── */}
          <Formik
            initialValues={{
              F_VehicleType: state.formData.F_VehicleType || "",
              RangeFrom: state.formData.RangeFrom || "",
              RangeTo: state.formData.RangeTo || "",
              BaseRate: state.formData.BaseRate || "",
              WaitingPeriod: state.formData.WaitingPeriod || "",
              WaitingPeriodCharge: state.formData.WaitingPeriodCharge || "",
              PointWiseRate: state.formData.PointWiseRate || "",
              LoadingUnloadingCharge: state.formData.LoadingUnloadingCharge ?? "",
              FreeLoadingTime: state.formData.FreeLoadingTime ?? "",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ errors, touched, setFieldValue }) => (
              <Form id="vehicle-rate-form">

                {/* ── Vehicle Type (full row) ── */}
                <div className="form-group mb-2">
                  <label style={labelStyle}>
                    Vehicle Type <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <Field
                    innerRef={vehicleTypeRef}
                    as="select"
                    name="F_VehicleType"
                    className={`form-control form-control-sm ${errors.F_VehicleType && touched.F_VehicleType ? "is-invalid" : ""}`}
                    style={inputStyle}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFieldValue("F_VehicleType", val);
                      fetchDataByVehicleType(val);
                    }}
                  >
                    <option value="">— Select Vehicle Type —</option>
                    {vehicleTypeList.map((vt) => (
                      <option key={vt.Id} value={vt.Id}>
                        {vt.Name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="F_VehicleType" component="div" style={errorStyle} />
                </div>

                {/* ── Row 1: RangeFrom | RangeTo ── */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0 12px",
                  }}
                >
                  <div className="form-group mb-2">
                    <label style={labelStyle}>
                      Range From (km) <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <Field
                      type="number"
                      name="RangeFrom"
                      step="0.01"
                      min="0"
                      placeholder="e.g. 0"
                      className={`form-control form-control-sm ${errors.RangeFrom && touched.RangeFrom ? "is-invalid" : ""}`}
                      style={inputStyle}
                    />
                    <ErrorMessage name="RangeFrom" component="div" style={errorStyle} />
                  </div>

                  <div className="form-group mb-2">
                    <label style={labelStyle}>
                      Range To (km) <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <Field
                      type="number"
                      name="RangeTo"
                      step="0.01"
                      min="0"
                      placeholder="e.g. 50"
                      className={`form-control form-control-sm ${errors.RangeTo && touched.RangeTo ? "is-invalid" : ""}`}
                      style={inputStyle}
                    />
                    <ErrorMessage name="RangeTo" component="div" style={errorStyle} />
                  </div>
                </div>

                {/* ── Row 2: BaseRate | WaitingPeriod ── */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0 12px",
                  }}
                >
                  <div className="form-group mb-2">
                    <label style={labelStyle}>
                      Base Rate (₹) <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <Field
                      type="number"
                      name="BaseRate"
                      step="0.01"
                      min="0"
                      placeholder="e.g. 100.00"
                      className={`form-control form-control-sm ${errors.BaseRate && touched.BaseRate ? "is-invalid" : ""}`}
                      style={inputStyle}
                    />
                    <ErrorMessage name="BaseRate" component="div" style={errorStyle} />
                  </div>

                  <div className="form-group mb-2">
                    <label style={labelStyle}>
                      Waiting Period (mins) <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <Field
                      type="number"
                      name="WaitingPeriod"
                      step="1"
                      min="0"
                      placeholder="e.g. 10"
                      className={`form-control form-control-sm ${errors.WaitingPeriod && touched.WaitingPeriod ? "is-invalid" : ""}`}
                      style={inputStyle}
                    />
                    <ErrorMessage name="WaitingPeriod" component="div" style={errorStyle} />
                  </div>
                </div>

                {/* ── Row 3: WaitingPeriodCharge | PointWiseRate ── */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0 12px",
                  }}
                >
                  <div className="form-group mb-2">
                    <label style={labelStyle}>
                      Waiting Period Charge (₹/min) <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <Field
                      type="number"
                      name="WaitingPeriodCharge"
                      step="0.01"
                      min="0"
                      placeholder="e.g. 2.00"
                      className={`form-control form-control-sm ${errors.WaitingPeriodCharge && touched.WaitingPeriodCharge ? "is-invalid" : ""}`}
                      style={inputStyle}
                    />
                    <ErrorMessage name="WaitingPeriodCharge" component="div" style={errorStyle} />
                  </div>

                  <div className="form-group mb-2">
                    <label style={labelStyle}>
                      Point Wise Rate (₹/point) <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <Field
                      type="number"
                      name="PointWiseRate"
                      step="0.01"
                      min="0"
                      placeholder="e.g. 5.00"
                      className={`form-control form-control-sm ${errors.PointWiseRate && touched.PointWiseRate ? "is-invalid" : ""}`}
                      style={inputStyle}
                    />
                    <ErrorMessage name="PointWiseRate" component="div" style={errorStyle} />
                  </div>
                </div>

                {/* ── Row 4: Loading & Unloading Charges (Labour) & Free Minutes ── */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0 12px",
                  }}
                >
                  <div className="form-group mb-2">
                    <label style={labelStyle}>
                      Loading & Unloading Charges (Labour ₹)
                    </label>
                    <Field
                      type="number"
                      name="LoadingUnloadingCharge"
                      step="0.01"
                      min="0"
                      placeholder="e.g. 150.00"
                      className={`form-control form-control-sm ${errors.LoadingUnloadingCharge && touched.LoadingUnloadingCharge ? "is-invalid" : ""}`}
                      style={inputStyle}
                    />
                    <small className="text-muted d-block" style={{ fontSize: 10 }}>
                      Extra labour charge for loading & unloading.
                    </small>
                    <ErrorMessage name="LoadingUnloadingCharge" component="div" style={errorStyle} />
                  </div>
                  <div className="form-group mb-2">
                    <label style={labelStyle}>
                      Free Loading/Unloading Time (Mins)
                    </label>
                    <Field
                      type="number"
                      name="FreeLoadingTime"
                      min="0"
                      placeholder="e.g. 15 (0 if none)"
                      className={`form-control form-control-sm ${errors.FreeLoadingTime && touched.FreeLoadingTime ? "is-invalid" : ""}`}
                      style={inputStyle}
                    />
                    <small className="text-muted d-block" style={{ fontSize: 10 }}>
                      App me banner me "Free X mins of loading-unloading time included" dikhega. 0 hone par hide hoga.
                    </small>
                    <ErrorMessage name="FreeLoadingTime" component="div" style={errorStyle} />
                  </div>
                </div>

                {/* ── Slab Table ── */}
                <div style={{ marginTop: 10 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 4,
                      paddingBottom: 3,
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#333" }}>
                      Distance Slabs (Rate per KM)
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      color="success"
                      className="py-0 px-2"
                      style={{ fontSize: 11 }}
                      onClick={addSlabRow}
                    >
                      <Plus size={11} className="me-1" />Add Row
                    </Button>
                  </div>

                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: "#E87B2D" }}>
                        <th style={thStyle}>#</th>
                        <th style={thStyle}>Range From (km)</th>
                        <th style={thStyle}>Range To (km)</th>
                        <th style={thStyle}>Rate Per KM (₹)</th>
                        <th style={thStyle}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {slabs.map((slab, index) => (
                        <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                          <td style={tdStyle}>{index + 1}</td>
                          <td style={tdStyle}>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={slab.RangeFrom}
                              onChange={(e) => handleSlabChange(index, "RangeFrom", e.target.value)}
                              placeholder="e.g. 51"
                              style={{ ...inputStyle, width: "100%", border: "1px solid #ced4da" }}
                            />
                          </td>
                          <td style={tdStyle}>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={slab.RangeTo}
                              onChange={(e) => handleSlabChange(index, "RangeTo", e.target.value)}
                              placeholder="e.g. 100"
                              style={{ ...inputStyle, width: "100%", border: "1px solid #ced4da" }}
                            />
                          </td>
                          <td style={tdStyle}>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={slab.RatePerKm}
                              onChange={(e) => handleSlabChange(index, "RatePerKm", e.target.value)}
                              placeholder="e.g. 8.00"
                              style={{ ...inputStyle, width: "100%", border: "1px solid #ced4da" }}
                            />
                          </td>
                          <td style={{ ...tdStyle, textAlign: "center" }}>
                            {slabs.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeSlabRow(index)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  color: "#dc3545",
                                  padding: 2,
                                }}
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* End Slab Table */}

              </Form>
            )}
          </Formik>

        </CardBody>
      </Card>
    </div>
  );
};

// Table cell styles
const thStyle = {
  padding: "4px 8px",
  color: "#fff",
  fontWeight: 600,
  fontSize: 11,
  border: "1px solid #d77a26",
};

const tdStyle = {
  padding: "3px 6px",
  borderBottom: "1px solid #dee2e6",
  verticalAlign: "middle",
};

export default AddEdit_VehicleRateMaster;
